import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filter } from "lodash";
import { useSnackbar } from "notistack";

// components (global)
import ButtonProgress from "components/ButtonProgress";

// firebase
import { useFirestore } from "react-redux-firebase";

// form
import { useForm } from "react-hook-form";

// helpers
import { deviceValidate } from "helpers/validation/deviceValidate";

// material-ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// stores
import { toggleAddServiceDialogClose } from "stores/setting/SettingAction";

// styles
const useStyles = makeStyles(theme => ({
  root: {}
}));

const AddServiceDialog = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const firestore = useFirestore();
  const { errors, handleSubmit, register, setError } = useForm();

  // state
  const [submitting, setSubmitting] = useState(false);

  // selector
  const { open, id: userId } = useSelector(
    state => state.setting.add_service_dialog
  );
  const uid = useSelector(state => state.firebase.auth.uid);
  const profile = useSelector(state => state.firebase.profile);
  const user = useSelector(state => {
    return state.firestore.data.guardUsers
      ? state.firestore.data.guardUsers[userId]
      : null;
  });

  // functions
  const handleClose = () => {
    if (!submitting) dispatch(toggleAddServiceDialogClose());
  };

  const onSubmit = ({ serviceId, portNo, type }) => {
    if (!submitting) {
      setSubmitting(true);

      const services = user.services ? user.services : [];

      const portExists = filter(services, service => {
        return service.tcp_port === parseInt(portNo);
      });

      if (portExists.length > 0) {
        setSubmitting(false);
        setError("portNo", "exists");
        return;
      }

      firestore
        .update(
          { collection: "ac_users", doc: userId },
          {
            ...user,
            updated_at: new Date(),
            updated_by: {
              user_id: uid,
              name: profile.name
            },
            services: [
              ...services,
              {
                service_id: serviceId,
                tcp_port: parseInt(portNo),
                type: type ? type.toLowerCase() : ""
              }
            ]
          }
        )
        .then(() => {
          setSubmitting(false);
          handleClose();
          snackbar.enqueueSnackbar("xSocket Service had been added", {
            variant: "success"
          });
        })
        .catch(e => {
          setSubmitting(false);
          handleClose();
          snackbar.enqueueSnackbar(e.message, {
            variant: "error",
            persist: false
          });
        });
    }
  };

  if (!user) return null;

  return (
    <div>
      <form
        noValidate
        xs={12}
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <Dialog
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          onClose={handleClose}
          open={open}
          fullWidth={true}
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle id="alert-dialog-title" className={classes.modalTitle}>
            <div>
              <Typography
                className={classes.modalTitle}
                variant="h3"
                component="h2"
              >
                Add xSocket Service
              </Typography>
            </div>
          </DialogTitle>

          <DialogContent dividers>
            <Grid
              container
              spacing={2}
              direction="row"
              justify="center"
              alignItems="flex-start"
            >
              <Grid item md={4}>
                <TextField
                  fullWidth
                  size="small"
                  id="serviceId"
                  name="serviceId"
                  label="Service ID *"
                  type="text"
                  variant="outlined"
                  autoComplete="off"
                  inputRef={register(deviceValidate.serviceId.rules)}
                  helperText={
                    errors.serviceId &&
                    deviceValidate.serviceId.message[errors.serviceId.type]
                  }
                  error={!!errors.serviceId}
                />
              </Grid>
              <Grid item md={4}>
                <TextField
                  fullWidth
                  size="small"
                  id="portNo"
                  name="portNo"
                  label="Port No. *"
                  type="number"
                  variant="outlined"
                  autoComplete="off"
                  defaultValue={
                    user && user.services ? 2604 + user.services.length : 2604
                  }
                  inputRef={register(deviceValidate.portNo.rules)}
                  helperText={
                    errors.portNo &&
                    deviceValidate.portNo.message[errors.portNo.type]
                  }
                  error={!!errors.portNo}
                />
              </Grid>
              <Grid item md={4}>
                <TextField
                  fullWidth
                  size="small"
                  id="type"
                  name="type"
                  label="Type"
                  type="text"
                  variant="outlined"
                  autoComplete="off"
                  inputRef={register}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={handleClose}>
              Cancel
            </Button>
            <ButtonProgress
              type="submit"
              containName="Save"
              margin={false}
              loading={submitting}
              disabled={submitting}
              onClick={handleSubmit(onSubmit)}
            />
          </DialogActions>
        </Dialog>
      </form>
    </div>
  );
};

export default AddServiceDialog;
